import { makeAutoObservable } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { toastStore, type IDropdownItem } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { ClientsApi } from 'entities/Clients/api/clients'
import { ClientsPath } from 'entities/ExportRouters'
import { DropdownCardsStore } from 'entities/Billing/ui/DropdownCards'
import {
  type IResponseInviteClientCard,
  type IParamsInviteClient,
} from 'entities/Clients/api/types'
import { ClientDetailsStore } from './clientDetailsStore'
import { ChoosePlanStore } from './choosePlanStore'
import { CheckoutCardStore } from './checkoutCardStore'

export const PAYMENT_ERROR_MESSAGE = 'Please select a different card'
export class CreateStore {
  currentStep = 1
  clientDetailsStore: ClientDetailsStore
  choosePlanStore: ChoosePlanStore
  readonly checkoutCardStore: CheckoutCardStore
  isPaymentError = false
  isInviteLoading = false
  readonly dropdownCardsStore: DropdownCardsStore
  private _selectedPaymentOption: string | null = null

  constructor(private _entities: IEntitiesStore, private _duplicateId?: number) {
    this.clientDetailsStore = new ClientDetailsStore(this._entities, this._duplicateId)
    this.choosePlanStore = new ChoosePlanStore(this._entities)
    this.checkoutCardStore = new CheckoutCardStore()
    this.dropdownCardsStore = new DropdownCardsStore(this._entities.billingStore, {
      labelText: 'Card',
      enableAch: true,
      isFranchise: this.isFranchise,
    })
    makeAutoObservable(this)
  }

  init = async () => {
    const { billingStore } = this._entities

    if (billingStore.billingPlansStore.plans.length === 0) {
      await billingStore.fetchPlans()
    }
  }

  get totalSteps() {
    return this.isFranchise ? 2 : 3
  }

  get isFranchise() {
    return !!this._entities.organizationStore.franchise
  }

  get isFirstStep() {
    return this.currentStep === 1
  }

  get isLastStep() {
    return this.currentStep === this.totalSteps
  }

  get isGroupsFlowEnabled() {
    return this._entities.featureFlagsStore.groups_flow
  }

  get isContainerWide() {
    return this.currentStep === 2 && !this.isFranchise
  }

  get selectedPaymentOption() {
    return this._selectedPaymentOption ?? (this.isFranchise ? 'clientWillPay' : 'now')
  }

  get isPayNow() {
    return this.selectedPaymentOption === 'now'
  }

  get paymentOptions(): IDropdownItem[] {
    return [
      {
        id: 'now',
        label: 'Pay now',
      },
      {
        id: 'clientWillPay',
        label: 'Client will pay at sign up',
      },
    ]
  }

  setSelectedPaymentOption = (id: string) => {
    this._selectedPaymentOption = id
  }

  nextStep = () => {
    if (this.currentStep < this.totalSteps) {
      this.currentStep += 1
    }
  }

  previousStep = () => {
    if (this.currentStep > 1) {
      this.currentStep -= 1
    }
  }

  handleInviteClient = async () => {
    this.clearPaymentError()
    try {
      this.isInviteLoading = true

      let tokenData: {
        tokenId: string
        name: string
        card: IResponseInviteClientCard | string
      } | null = null
      if (this.isPayNow && !this.isFranchise) {
        tokenData = await this.checkoutCardStore.getToken()
        if (!tokenData) {
          this.isInviteLoading = false
          return
        }
      }

      const payload: IParamsInviteClient = {
        integration_teams: this.clientDetailsStore.teamsSelectFieldStore.selectedTeamsForApi || [],
        email: this.clientDetailsStore.clientInviteFieldStore.selectedEmail,
        plan: this.isFranchise
          ? this.choosePlanStore.franchisePlan
          : this.choosePlanStore.selectedPlan?.id || '',
        pay_now: this.isPayNow,
        trial: !this.isFranchise && this.choosePlanStore.startTrialChecked,
        card: this.isFranchise ? this.dropdownCardsStore.activeId ?? '' : tokenData?.card ?? '',
        interval: this.isFranchise
          ? this.choosePlanStore.franchiseInterval
          : this.choosePlanStore.period === 'annually'
          ? 'annual'
          : 'monthly',
        payment_method: this.isFranchise ? this.dropdownCardsStore?.activeId ?? '' : '',
        ...(tokenData && { card_token: tokenData.tokenId }),
        ...(tokenData && { name: tokenData.name }),
      }
      if (this.clientDetailsStore.selectedDuplicateClientId) {
        payload.organization_clone = this.clientDetailsStore.selectedDuplicateClientId.toString()
      }

      await ClientsApi.inviteClient(payload)

      toastStore.add({
        type: 'success',
        title: 'Client created',
      })
      uiStore.changeRoute({
        path: `${ClientsPath.root}`,
      })
    } catch (e) {
      logger.error(e)
      if (
        e instanceof AxiosError &&
        (e.errors.getError('message').startsWith('Your card ') ||
          e.errors.getError('error').startsWith('Your card ') ||
          e.errors.getError('card_error'))
      ) {
        this.isPaymentError = true
        const cardErrorMessage = e.errors.getError('card_error') || PAYMENT_ERROR_MESSAGE
        if (this.isFranchise) {
          this.dropdownCardsStore.setPaymentError(cardErrorMessage)
        } else {
          this.checkoutCardStore.setCardError(cardErrorMessage)
        }
      } else {
        toastStore.add({
          type: 'error',
          title: 'Failed to create client',
        })
      }
    } finally {
      this.isInviteLoading = false
    }
  }

  clearPaymentError = () => {
    this.isPaymentError = false
    this.dropdownCardsStore.clearPaymentError()
  }

  dispose = () => {
    this.clientDetailsStore.reset()
    this.choosePlanStore.resetCheckout()
    this.checkoutCardStore.reset()
    this._selectedPaymentOption = null
    this.currentStep = 1
  }
}
